import { useMemo } from 'react';
import type { ApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import useAbortRegistry from '@dr-pam/common-components/Hooks/useAbortRegistry';
import type ICrudService from '@dr-pam/common-components/Services/ICrudService';
import type { RegisterAbortFunction } from '@dr-pam/common-components/Utils/AbortUtils';
import ApolloUtils from '@dr-pam/common-components/Utils/ApolloUtils';
import type {
	CouponByCodeQuery,
	CouponByCodeQueryVariables,
	CouponCreateInput,
	CouponListFragment,
	CouponQuery,
	CouponQueryVariables,
	CouponSingleFragment,
	CouponUpdateInput,
	CouponUseCountsQuery,
	CouponUsesQuery,
	CouponUsesQueryVariables,
	CouponsFilteredQuery,
	CouponsFilteredQueryVariables,
	CouponsQuery,
	CreateCouponMutation,
	CreateCouponMutationVariables,
	DeleteCouponsMutation,
	DeleteCouponsMutationVariables,
	UpdateCouponMutation,
	UpdateCouponMutationVariables,
} from '../graphql/graphql';
import type IPublishable from '@dr-pam/common-components/Services/IPublishable';

export const GQL_FRAG_COUPON_PRODUCT_RESTRICTION = gql`
	fragment CouponProductRestriction on CouponProductRestriction {
		id
		couponId
		productId
		product {
			name
			shortName
		}
	}
`;

export const GQL_FRAG_COUPON_LIST = gql`
	${GQL_FRAG_COUPON_PRODUCT_RESTRICTION}
	fragment CouponList on Coupon {
		id
		isPublished
		isArchived
		code
		description
		percentage
		fixedAmountInCents
		discountType
		validFrom
		validTo
		maxUses
		# Usage counts are NOT fetched here — see GQL_GET_COUPON_USE_COUNTS. The generator emits _count
		# sub-fields without arguments, so a filtered relation count cannot be expressed on the coupon
		# itself, and fetching the payment rows to count them client-side scales with payment volume.
		restrictedToProducts {
			...CouponProductRestriction
		}
	}
`;

export const GQL_FRAG_COUPON_SINGLE = gql`
	${GQL_FRAG_COUPON_PRODUCT_RESTRICTION}
	fragment CouponSingle on Coupon {
		id
		isPublished
		isArchived
		code
		description
		percentage
		fixedAmountInCents
		discountType
		validFrom
		validTo
		maxUses
		restrictedToProducts {
			...CouponProductRestriction
		}
	}
`;

export const GQL_GET_COUPON = gql`
	${GQL_FRAG_COUPON_SINGLE}
	query Coupon($couponId: String!) {
		coupon(where: { id: $couponId }) {
			...CouponSingle
		}
	}
`;

export const GQL_GET_COUPON_BY_CODE = gql`
	${GQL_FRAG_COUPON_SINGLE}
	query CouponByCode($couponCode: String!) {
		coupon(where: { code: $couponCode }) {
			...CouponSingle
		}
	}
`;

export const GQL_GET_COUPONS_FOR_LIST = gql`
	${GQL_FRAG_COUPON_LIST}
	query Coupons {
		coupons {
			...CouponList
		}
	}
`;

export const GQL_GET_COUPONS_FOR_LIST_FILTERED = gql`
	${GQL_FRAG_COUPON_LIST}
	query CouponsFiltered($where: CouponWhereInput) {
		coupons(where: $where) {
			...CouponList
		}
	}
`;

/**
 * Completed-payment counts for every coupon, in one query.
 *
 * A filtered relation count (`_count { usedInPayments(where: …) }`) is no longer expressible: the
 * generator emits _count sub-fields without arguments, and adding them back does not help because
 * paljs PrismaSelect discards args on leaf fields, so the filter would be silently ignored.
 *
 * groupBy does the counting in SQL and returns one row per coupon, so the cost is O(coupons) and
 * flat in payment volume — unlike fetching each coupon's payment rows to count them client-side.
 */
export const GQL_GET_COUPON_USE_COUNTS = gql`
	query CouponUseCounts {
		groupByPayment(
			by: [couponId]
			where: { couponId: { not: null }, stripePaymentIntents: { some: { status: { equals: SUCCEEDED } } } }
		) {
			couponId
			_count {
				_all
			}
		}
	}
`;

// Counted via aggregatePayment rather than Coupon._count.usedInPayments: the current generator emits
// _count sub-fields without arguments, so a filtered relation count is no longer expressible there.
// The filter is not optional — "uses" means completed payments only.
export const GQL_GET_COUPON_USES = gql`
	query CouponUses($couponId: String!) {
		aggregatePayment(
			where: {
				couponId: { equals: $couponId }
				stripePaymentIntents: { some: { status: { equals: SUCCEEDED } } }
			}
		) {
			_count {
				_all
			}
		}
	}
`;

export const GQL_DELETE_COUPONS = gql`
	mutation DeleteCoupons($couponIds: [String!]) {
		deleteManyCoupon(where: { id: { in: $couponIds } }) {
			count
		}
	}
`;

export const GQL_CREATE_COUPON = gql`
	${GQL_FRAG_COUPON_SINGLE}
	mutation CreateCoupon($coupon: CouponCreateInput!) {
		createOneCoupon(data: $coupon) {
			...CouponSingle
		}
	}
`;

export const GQL_UPDATE_COUPON = gql`
	${GQL_FRAG_COUPON_SINGLE}
	mutation UpdateCoupon($couponId: String!, $coupon: CouponUpdateInput!) {
		updateOneCoupon(where: { id: $couponId }, data: $coupon) {
			...CouponSingle
		}
	}
`;

export default class CouponService
	implements ICrudService<CouponListFragment, CouponSingleFragment>, IPublishable<CouponListFragment>
{
	constructor(
		private readonly _apolloClient: ApolloClient,
		private readonly _registerAbort?: RegisterAbortFunction,
	) {}

	public async publish(couponId: string): Promise<CouponListFragment> {
		return await this.update(couponId, { isPublished: { set: true } });
	}

	public async unpublish(couponId: string): Promise<CouponListFragment> {
		return await this.update(couponId, { isPublished: { set: false } });
	}

	public get(couponId: string) {
		const request = ApolloUtils.abortableQuery<CouponQuery, CouponSingleFragment | null, CouponQueryVariables>(
			this._apolloClient,
			{
				query: GQL_GET_COUPON,
				variables: {
					couponId,
				},
			},
			(data) => data.coupon ?? null,
			this._registerAbort,
		);

		return request;
	}

	public getByCode(couponCode: string) {
		const request = ApolloUtils.abortableQuery<
			CouponByCodeQuery,
			CouponSingleFragment | null,
			CouponByCodeQueryVariables
		>(
			this._apolloClient,
			{
				query: GQL_GET_COUPON_BY_CODE,
				variables: {
					couponCode,
				},
			},
			(data) => data.coupon ?? null,
			this._registerAbort,
		);

		return request;
	}

	/** Completed-payment counts keyed by coupon id. Coupons with no completed payments are absent. */
	public getUseCounts() {
		return ApolloUtils.abortableQuery<CouponUseCountsQuery, Map<string, number>>(
			this._apolloClient,
			{
				query: GQL_GET_COUPON_USE_COUNTS,
			},
			(data) =>
				new Map(
					data.groupByPayment
						.filter((group) => group.couponId != null)
						.map((group) => [group.couponId as string, group._count?._all ?? 0]),
				),
			this._registerAbort,
		);
	}

	public getCouponUses(couponId: string) {
		const request = ApolloUtils.abortableQuery<CouponUsesQuery, number, CouponUsesQueryVariables>(
			this._apolloClient,
			{
				query: GQL_GET_COUPON_USES,
				variables: {
					couponId,
				},
			},
			(data) => data.aggregatePayment._count?._all ?? 0,
			this._registerAbort,
		);
		return request;
	}

	public getAll(includeArchived = false) {
		if (includeArchived) {
			const request = ApolloUtils.abortableQuery<CouponsQuery, CouponListFragment[]>(
				this._apolloClient,
				{
					query: GQL_GET_COUPONS_FOR_LIST,
				},
				(data) => data.coupons,
				this._registerAbort,
			);
			return request;
		}

		const request = ApolloUtils.abortableQuery<
			CouponsFilteredQuery,
			CouponListFragment[],
			CouponsFilteredQueryVariables
		>(
			this._apolloClient,
			{
				query: GQL_GET_COUPONS_FOR_LIST_FILTERED,
				variables: {
					where: {
						isArchived: {
							equals: false,
						},
					},
				},
			},
			(data) => data.coupons,
			this._registerAbort,
		);

		return request;
	}

	public getArchived() {
		const request = ApolloUtils.abortableQuery<
			CouponsFilteredQuery,
			CouponListFragment[],
			CouponsFilteredQueryVariables
		>(
			this._apolloClient,
			{
				query: GQL_GET_COUPONS_FOR_LIST_FILTERED,
				variables: {
					where: {
						isArchived: {
							equals: true,
						},
					},
				},
			},
			(data) => data.coupons,
			this._registerAbort,
		);

		return request;
	}

	public async create(coupon: CouponCreateInput) {
		const result = await this._apolloClient.mutate<CreateCouponMutation, CreateCouponMutationVariables>({
			mutation: GQL_CREATE_COUPON,
			variables: {
				coupon,
			},
		});
		if (!result.data?.createOneCoupon) {
			throw new Error('Failed to create coupon');
		}
		return result.data.createOneCoupon;
	}

	public async update(couponId: string, coupon: CouponUpdateInput) {
		const result = await this._apolloClient.mutate<UpdateCouponMutation, UpdateCouponMutationVariables>({
			mutation: GQL_UPDATE_COUPON,
			variables: {
				couponId,
				coupon,
			},
		});
		if (!result.data?.updateOneCoupon) {
			throw new Error('Failed to update coupon');
		}
		return result.data.updateOneCoupon;
	}

	public async delete(couponIds: string | string[]) {
		couponIds = Array.isArray(couponIds) ? couponIds : [couponIds];
		await this._apolloClient.mutate<DeleteCouponsMutation, DeleteCouponsMutationVariables>({
			mutation: GQL_DELETE_COUPONS,
			variables: {
				couponIds,
			},
		});
	}
}

export function useCouponService() {
	const apolloClient = useApolloClient();
	const registerAbort = useAbortRegistry();

	return useMemo(() => new CouponService(apolloClient, registerAbort), [apolloClient, registerAbort]);
}
